// HistoryReception.jsx - Versiunea fără bară de derulare orizontală
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../Config/axiosInstance";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import { Typography, Tabs, Tab, Box, TextField, CircularProgress, Button } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { NumericFormat } from "react-number-format";
import DescriptionIcon from '@mui/icons-material/Description';
import "./HistoryReception.css";

const HistoryReception = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [receptionGroup, setReceptionGroup] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRevisionId, setSelectedRevisionId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  const [reception, setReception] = useState(null);
  const [items, setItems] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [receptionNote, setReceptionNote] = useState('');
  const [historyByDate, setHistoryByDate] = useState({});

  const normalizeDecimal = (value) => {
    if(value === null || value === undefined || value === "") return "";
    const number = parseFloat(value);
    if(isNaN(number)) return "";
    return new Intl.NumberFormat('ro-RO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4
    }).format(number);
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'received': 'received',
      'partially received': 'partially-received',
      'not received': 'not-received',
      'canceled': 'canceled'
    };
    return statusMap[status?.toLowerCase()] || status?.toLowerCase().replace(' ', '-');
  };

  const extractProductName = (productNameObj) => {
    if (!productNameObj) return 'N/A';
    if (typeof productNameObj === 'string') return productNameObj;
    if (typeof productNameObj === 'object') {
      return productNameObj.name || productNameObj.title || productNameObj.product_name || 'Unknown Product';
    }
    return String(productNameObj);
  };

  const groupHistoryByDateAndTime = (historyArray) => {
    const grouped = {};
    const sortedHistory = [...historyArray].sort((a, b) => new Date(a.change_date) - new Date(b.change_date));
    
    sortedHistory.forEach(entry => {
      const dateObj = new Date(entry.change_date);
      const dateKey = dateObj.toLocaleDateString('en-EN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      const timeKey = dateObj.toLocaleTimeString('ro-RO', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      const dateTimeKey = `${dateKey} - ${timeKey}`;
      
      if (!grouped[dateTimeKey]) grouped[dateTimeKey] = [];
      grouped[dateTimeKey].push(entry);
    });
    
    const sortedGrouped = {};
    Object.keys(grouped).sort((a, b) => {
      const dateA = new Date(a.split(' - ')[0] + ' ' + a.split(' - ')[1].replace(/\./g, ':'));
      const dateB = new Date(b.split(' - ')[0] + ' ' + b.split(' - ')[1].replace(/\./g, ':'));
      return dateB - dateA;
    }).forEach(key => { sortedGrouped[key] = grouped[key]; });
    
    return sortedGrouped;
  };

  const calculateTotalReceived = (productId) => {
    let total = 0;
    Object.values(historyByDate).forEach(dateTimeEntries => {
      dateTimeEntries.forEach(entry => {
        if (entry.product_id === productId) {
          total += parseFloat(entry.qty_received);
        }
      });
    });
    return total;
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const response = await axiosInstance.get(`/reception-documents/${documentId}/download/`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const setReceptionDataFromGroup = useCallback((receptionData) => {
    setReception({
      id: receptionData.id,
      po_number: receptionData.po_number,
      status: receptionData.status,
      delivery_date: receptionData.delivery_date,
      created_at: receptionData.created_at,
      updated_at: receptionData.updated_at,
      reception_note: receptionData.reception_note
    });
    
    setReceptionNote(receptionData.reception_note || '');
    setExistingDocuments(receptionData.documents || []);
    
    const processedItems = (receptionData.items || []).map(item => {
      let productName = extractProductName(item.product_name);
      
      return {
        id: item.id,
        product_name: productName,
        product_id: item.product_id,
        qty_ordered: parseFloat(item.qty_ordered),
        qty_received: parseFloat(item.qty_received),
        remaining_to_deliver: parseFloat(item.remaining_to_deliver)
      };
    });
    setItems(processedItems);
    
    let combinedHistory = [];
    (receptionData.items || []).forEach(item => {
      if (item.history && item.history.length > 0) {
        const historyWithProduct = item.history.map(entry => {
          let productName = extractProductName(entry.product_name || item.product_name);
          
          return {
            id: entry.id,
            product_name: productName,
            product_id: item.id,
            qty_ordered: parseFloat(entry.qty_ordered),
            qty_received: parseFloat(entry.qty_received),
            change_date: entry.change_date
          };
        });
        combinedHistory = [...combinedHistory, ...historyWithProduct];
      }
    });
    
    combinedHistory.sort((a, b) => new Date(a.change_date) - new Date(b.change_date));
    setHistoryByDate(groupHistoryByDateAndTime(combinedHistory));
  }, []);

  const loadReceptionGroup = useCallback(async (receptionId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axiosInstance.get(`/reception/${receptionId}/group-history-full/`);
      const groupData = response.data;
      
      if (groupData && groupData.length > 0) {
        setReceptionGroup(groupData);
        
        const latestIndex = groupData.findIndex(r => r.is_latest === true);
        const targetIndex = latestIndex !== -1 ? latestIndex : 0;
        const targetRevision = groupData[targetIndex];
        
        setSelectedRevisionId(targetRevision.id);
        setActiveTab(targetIndex);
        setReceptionDataFromGroup(targetRevision);
      } else {
        setError("No reception data found");
      }
    } catch (error) {
      console.error("Error loading reception group:", error);
      setError("Failed to load reception history. Please check if the reception exists.");
    } finally {
      setLoading(false);
    }
  }, [setReceptionDataFromGroup]);

  const handleTabChange = (event, newValue) => {
    if (newValue === activeTab) return;
    const selectedRevision = receptionGroup[newValue];
    if (!selectedRevision || selectedRevision.id === selectedRevisionId) return;
    
    setActiveTab(newValue);
    setSelectedRevisionId(selectedRevision.id);
    setReceptionDataFromGroup(selectedRevision);
  };

  useEffect(() => {
    if (id) {
      loadReceptionGroup(id);
    }
  }, [id, loadReceptionGroup]);

  if (loading && !reception) {
    return (
      <div className="history-reception">
        <div className="history-reception-container">
          <div className="reception-header">
            <ArrowBackIcon className='back-arrow' onClick={() => navigate('/reception')} />
            <h2 className='history-reception-title'>Reception History</h2>
          </div>
          <hr className="title-separator" />
          <div className="loading-container">
            <CircularProgress />
            <Typography>Loading reception history...</Typography>
          </div>
        </div>
      </div>
    );
  }

  if (error && !reception) {
    return (
      <div className="history-reception">
        <div className="history-reception-container">
          <div className="reception-header">
            <ArrowBackIcon className='back-arrow' onClick={() => navigate('/reception')} />
            <h2 className='history-reception-title'>Reception History</h2>
          </div>
          <hr className="title-separator" />
          <div className="error-container">
            <Typography color="error">{error}</Typography>
            <Button variant="contained" onClick={() => navigate('/reception')} sx={{ mt: 2 }}>
              Back to Reception List
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="history-reception">
      <div className="history-reception-container">
        <div className="reception-header">
          <ArrowBackIcon className='back-arrow' onClick={() => navigate('/reception')} />
          <h2 className='history-reception-title'>Reception History</h2>
          {reception && (
            <span className={`reception-status-badge reception-status-${getStatusClass(reception.status)}`}>
              {reception.status || 'Unknown'}
            </span>
          )}
        </div>

        <hr className="title-separator" />

        <div className="add-reception-form">
          {receptionGroup.length > 1 && (
            <div className="po-button-container">
              <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%', mb: 2 }}>
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange} 
                  variant="scrollable" 
                  scrollButtons="auto"
                  className="revision-tabs"
                >
                  {receptionGroup.map((rev, idx) => (
                    <Tab key={rev.id} label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <span className="tab-po-number">{rev.po_number}</span>
                        {rev.is_latest && <span className="latest-badge">LATEST</span>}
                      </Box>
                    } />
                  ))}
                </Tabs>
              </Box>
            </div>
          )}

          <div className="po-button-container">
            <h3>PO NUMBER: {reception?.po_number || 'N/A'}</h3>
          </div>

          {reception && (
            <div className="reception-layout">
              <div className="row-1">
                <div className='items-card'>
                  <h4>ITEMS</h4>
                  <div className="table-container">
                    <table className="reception-table">
                      <thead>
                        <tr>
                          <th>ITEM</th>
                          <th>QTY ORDERED</th>
                          <th>TOTAL RECEIVED</th>
                          <th>REMAINING TO DELIVER</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => {
                          const totalReceived = calculateTotalReceived(item.id);
                          const remaining = Math.max(0, item.qty_ordered - totalReceived);
                          return (
                            <tr key={item.id}>
                              <td className="product-name-cell">{item.product_name}</td>
                              <td className="quantity-cell">
                                <NumericFormat
                                  value={normalizeDecimal(item.qty_ordered)}
                                  displayType="text"
                                  decimalScale={4}
                                  thousandSeparator="."
                                  decimalSeparator=","
                                />
                              </td>
                              <td className="quantity-cell">
                                <NumericFormat
                                  value={normalizeDecimal(totalReceived)}
                                  displayType="text"
                                  decimalScale={4}
                                  thousandSeparator="."
                                  decimalSeparator=","
                                />
                              </td>
                              <td className="quantity-cell">
                                <NumericFormat
                                  value={normalizeDecimal(remaining)}
                                  displayType="text"
                                  decimalScale={4}
                                  thousandSeparator="."
                                  decimalSeparator=","
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className='attach-card'>
                  <h4>ATTACH DOCUMENTS</h4>
                  <div className="attach-content">
                    {existingDocuments.length > 0 ? (
                      <div className="documents-list">
                        {existingDocuments.map((doc) => (
                          <div key={doc.id} className="document-item">
                            <AttachFileIcon fontSize="small" sx={{ color: '#1976d2', flexShrink: 0 }} />
                            <button
                              type="button"
                              onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                              className="document-link"
                            >
                              {doc.filename}
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Typography variant="body2" color="textSecondary" className="no-documents">
                        No documents attached
                      </Typography>
                    )}
                  </div>
                </div>
              </div>

              <div className="row-2">
                <div className="history-card">
                  <div className="history-header">
                    <HistoryIcon sx={{ color: '#1976d2' }} />
                    <Typography variant="h7" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      RECEPTION HISTORY
                    </Typography>
                  </div>
                  <div className="history-content">
                    {Object.keys(historyByDate).length > 0 ? (
                      <div className="timeline-container">
                        {Object.entries(historyByDate).map(([dateTime, entries]) => (
                          <div key={dateTime} className="timeline-group">
                            <div className="timeline-date">
                              <div className="date-badge">
                                {dateTime.split(' - ')[0]}
                                <span className="time">{dateTime.split(' - ')[1]}</span>
                              </div>
                            </div>
                            <div className="timeline-entries">
                              {entries.map((entry, idx) => (
                                <div key={entry.id || idx} className="timeline-entry">
                                  <div className="entry-content">
                                    <div className="entry-product">{entry.product_name}</div>
                                    <div className="entry-quantity">
                                      <span className="quantity">
                                        <NumericFormat 
                                          value={entry.qty_received} 
                                          displayType="text" 
                                          decimalScale={4}
                                          thousandSeparator="."
                                          decimalSeparator=","
                                        />
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-history">No reception history available</div>
                    )}
                  </div>
                </div>

                <div className="notes-card">
                  <div className="documents-header">
                    <DescriptionIcon sx={{color: '#1976d2'}}/>
                    <Typography variant="h7" sx={{fontWeight: 600, color:'#1976d2'}}>
                      RECEPTION NOTES
                    </Typography>
                  </div>
                  <div className="documents-content">
                    <TextField 
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      placeholder="No notes available"
                      value={receptionNote || ''}
                      InputProps={{
                        readOnly: true,
                      }}
                      className="reception-notes-textarea-readonly"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#f5f5f5',
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryReception;